import { useEffect, useRef, useState } from 'react'

const layoutKey = 'titam.pipeline.chat.pinned-layout'
let savedLayout = null
const readLayout = () => {
  try {
    const value = JSON.parse(localStorage.getItem(layoutKey))
    if (value && ['width', 'height', 'left', 'top'].every((key) => Number.isFinite(value[key])) && value.width > 0 && value.height > 0) return value
  } catch { /* A preferência também funciona em memória sem armazenamento. */ }
  return savedLayout
}

const fit = (box) => {
  const maxW = Math.max(1, window.innerWidth - 24)
  const maxH = Math.max(1, window.innerHeight - 24)
  const width = Math.max(Math.min(340, maxW), Math.min(box.width, maxW))
  const height = Math.max(Math.min(360, maxH), Math.min(box.height, maxH))
  return { width, height, left: Math.max(12, Math.min(box.left, window.innerWidth - width - 12)), top: Math.max(12, Math.min(box.top, window.innerHeight - height - 12)) }
}

const initialBox = () => fit({ width: 370, height: 480, left: window.innerWidth - 394, top: window.innerHeight - 500 })

const resizeBox = (old, mode, dx, dy) => {
  const minW = Math.min(340, window.innerWidth - 24)
  const minH = Math.min(360, window.innerHeight - 24)
  const left = mode === 'sw' ? Math.max(12, Math.min(old.left + dx, old.left + old.width - minW)) : old.left
  const top = mode === 'ne' ? Math.max(12, Math.min(old.top + dy, old.top + old.height - minH)) : old.top
  const width = mode === 'sw' ? old.width + old.left - left : Math.min(old.width + dx, window.innerWidth - old.left - 12)
  const height = mode === 'ne' ? old.height + old.top - top : Math.min(old.height + dy, window.innerHeight - old.top - 12)
  return fit({ left, top, width, height })
}

export default function useFloatingChat() {
  const [initialLayout] = useState(readLayout)
  const [pinned, setPinned] = useState(Boolean(initialLayout))
  const [box, setBox] = useState(() => initialLayout ? fit(initialLayout) : initialBox())
  useEffect(() => {
    savedLayout = pinned ? box : null
    try {
      if (pinned) localStorage.setItem(layoutKey, JSON.stringify(box))
      else localStorage.removeItem(layoutKey)
    } catch { /* Não impedir o chat quando o armazenamento estiver bloqueado. */ }
  }, [pinned, box])
  const gesture = useRef(null)
  useEffect(() => {
    const resize = () => { gesture.current = null; setBox((current) => fit(current)) }
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])
  const start = (event, mode) => {
    if (event.button !== 0 || (mode === 'move' && event.target.closest('button,a,input,select,textarea'))) return
    event.preventDefault()
    gesture.current = { mode, x: event.clientX, y: event.clientY, box }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const move = (event) => {
    const active = gesture.current
    if (!active) return
    const dx = event.clientX - active.x, dy = event.clientY - active.y
    const old = active.box
    if (active.mode === 'move') setBox(fit({ ...old, left: old.left + dx, top: old.top + dy }))
    else setBox(resizeBox(old, active.mode, dx, dy))
  }
  const bindings = (mode) => ({
    onPointerDown: (event) => start(event, mode), onPointerMove: move,
    onPointerUp: () => { gesture.current = null }, onPointerCancel: () => { gesture.current = null }, onLostPointerCapture: () => { gesture.current = null },
    onKeyDown: (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) || event.target !== event.currentTarget) return
      event.preventDefault()
      const dx = event.key === 'ArrowRight' ? 20 : event.key === 'ArrowLeft' ? -20 : 0
      const dy = event.key === 'ArrowDown' ? 20 : event.key === 'ArrowUp' ? -20 : 0
      setBox((current) => mode === 'move' ? fit({ ...current, left: current.left + dx, top: current.top + dy }) : resizeBox(current, mode, dx, dy))
    },
  })
  const reset = () => { gesture.current = null; setBox(initialBox()) }
  return { style: { ...box, right: 'auto', bottom: 'auto' }, bindings, pinned, togglePinned: () => setPinned((current) => !current), reset }
}
